import '../../styles/fonts.css';
import React, { useState, useRef, useEffect } from 'react';
import { generateTempId } from '../../utils/formatters';
import { EmojiPicker } from './EmojiPicker';
import { CustomEmoji, filterAllowedEmojis, unicodeToCustomEmoji, emojiRegex } from '../../utils/emojiMapping';
import { EmojiRenderer } from '../common/EmojiRenderer';
import SendIcon from '../../assets/send.png';
import SmileIcon from '../../assets/emojis/big_bright_smile.gif';

interface MessageInputProps {
  onSendMessage: (content: string, tempId: string) => void;
  onStartTyping: () => void;
  onStopTyping: () => void;
  currentUserNumber: string;
  isDesktop?: boolean;
}

const hasCustomEmojis = (text: string): boolean => {
  if (!text) return false;

  emojiRegex.lastIndex = 0;
  let match;

  while ((match = emojiRegex.exec(text)) !== null) {
    const unicodeEmoji = match[0];
    if (unicodeToCustomEmoji.has(unicodeEmoji)) {
      return true;
    }
  }

  return false;
};

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onStartTyping,
  onStopTyping,
  currentUserNumber,
  isDesktop = false,
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showRichText, setShowRichText] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const richTextRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = filterAllowedEmojis(e.target.value);
    setMessage(value);

    setShowRichText(hasCustomEmojis(value));

    if (isDesktop) {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        const maxHeight = 120;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';

        if (richTextRef.current) {
          richTextRef.current.style.height = `${newHeight}px`;
        }
      }
    }

    if (!isTyping && value.trim()) {
      setIsTyping(true);
      onStartTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      onStopTyping();
    }, 1000);
  };

  const handleEmojiSelect = (emoji: CustomEmoji) => {
    const emojiToInsert = emoji.unicodeEmojis[0] || '😊';

    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage = message.slice(0, start) + emojiToInsert + message.slice(end);

      setMessage(newMessage);

      setShowRichText(hasCustomEmojis(newMessage));

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emojiToInsert.length;
        textarea.focus();
      }, 0);

      if (isDesktop) {
        setTimeout(() => {
          textarea.style.height = 'auto';
          const maxHeight = 120;
          const newHeight = Math.min(textarea.scrollHeight, maxHeight);
          textarea.style.height = `${newHeight}px`;
          textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
        }, 0);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const tempId = generateTempId();
    onSendMessage(trimmedMessage, tempId);
    setMessage('');
    setShowRichText(false);

    if (isTyping) {
      setIsTyping(false);
      onStopTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (isDesktop && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isDesktop) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleScroll = () => {
    if (richTextRef.current && textareaRef.current) {
      richTextRef.current.scrollTop = textareaRef.current.scrollTop;
      richTextRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const isMessageEmpty = !message.trim();

  return (
    <div
      style={{
        position: 'relative',
        padding: '12px',
        backgroundColor: '#fff',
        borderTop: isDesktop ? 'none' : '2px solid #000',
        paddingRight: isDesktop ? '10%' : '12px',
        paddingLeft: isDesktop ? '10%' : '12px',
        paddingBottom: isDesktop ? '12px' : `max(12px, env(safe-area-inset-bottom))`,
      }}
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <div
            style={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              border: isDesktop ? '2px solid #000' : 'none',
            }}
          >
            {/* Кнопка выбора эмодзи */}
            <EmojiPicker onEmojiSelect={handleEmojiSelect} isDesktop={isDesktop} />
            <div style={{ position: 'relative', flex: 1 }}>
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                placeholder={isDesktop ? 'Напиши что-нибудь...' : 'Напиши что-нибудь...'}
                style={{
                  width: '100%',
                  padding: isDesktop ? '12px' : '8px',
                  border: 'none',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  backgroundColor: showRichText ? 'transparent' : 'white',
                  color: showRichText ? 'transparent' : '#000',
                  outline: 'none',
                  resize: 'none' as const,
                  height: isDesktop ? 'auto' : '60px',
                  minHeight: isDesktop ? '20px' : '60px',
                  maxHeight: isDesktop ? '120px' : '60px',
                  overflowY: 'auto',
                  lineHeight: '1.4',
                  borderRadius: isDesktop ? '0' : '8px',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  WebkitTextSizeAdjust: '100%',
                  textSizeAdjust: '100%',
                  caretColor: showRichText ? '#000' : 'auto',
                }}
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                spellCheck='false'
                rows={isDesktop ? 1 : undefined}
              />

              {/* Rich text overlay с кастомными эмодзи */}
              {showRichText && (
                <div
                  ref={richTextRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    padding: isDesktop ? '12px' : '8px',
                    fontFamily: 'monospace',
                    fontSize: '16px',
                    lineHeight: '1.4',
                    color: '#000',
                    pointerEvents: 'none',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    overflowY: isDesktop ? 'hidden' : 'auto',
                    height: isDesktop ? 'auto' : '60px',
                    minHeight: isDesktop ? '20px' : '60px',
                    maxHeight: isDesktop ? '120px' : '60px',
                    zIndex: 1,
                  }}
                >
                  <EmojiRenderer text={message} isInInput={true} />
                </div>
              )}
            </div>
          </div>

          <button
            type='submit'
            disabled={isMessageEmpty}
            style={{
              background: isDesktop ? '#000' : '#00000000',
              border: isDesktop ? '2px solid #000' : 'none',
              cursor: !isMessageEmpty ? 'pointer' : 'not-allowed',
              padding: isDesktop ? '12px' : '8px',
              color: '#fff',
              fontFamily: 'monospace',
              fontSize: isDesktop ? '14px' : '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isDesktop ? 'auto' : '44px',
              minWidth: isDesktop ? 'auto' : '44px',
              borderRadius: isDesktop ? '0' : '22px',
              opacity: !isMessageEmpty ? 1 : 0.5,
              transition: 'opacity 0.2s ease',
            }}
          >
            {isDesktop ? (
              'ОТПРАВИТЬ'
            ) : (
              <img
                src={SendIcon}
                alt='Mario'
                style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = '➡';
                }}
              />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
